import { HttpService } from "../../modules/shared/services/http.service";
import { 
  LocationCountry, 
  CountryCreateDto, 
  LocationRegion, 
  RegionCreateDto, 
  LocationCity, 
  CityCreateDto,
  CountryBulkCreateDto,
  CountryResponse,
  RegionResponse,
  CityResponse
} from "../../modules/shared/model";

const headers = { "Content-Type": "application/json", "x-custom-lang": "en" } as const;

// Countries CRUD Functions
export const createCountry = async (data: CountryCreateDto): Promise<LocationCountry> => {
  const response = await HttpService.post("api/v1/countries", data, headers, true);
  return response;
};

export const updateCountry = async (id: string, data: Partial<CountryCreateDto>): Promise<LocationCountry> => {
  const response = await HttpService.patch(`api/v1/countries/${id}`, data, headers, true);
  return response;
};

export const getCountryById = async (id: string): Promise<LocationCountry> => {
  const response = await HttpService.get(`api/v1/countries/${id}`, true, { "x-custom-lang": "en" });
  return response;
};

export const getCountries = async (query?: Record<string, string | number | boolean>): Promise<CountryResponse> => {
  const qs = new URLSearchParams();
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.append(k, String(v));
  });
  const response = await HttpService.get(`api/v1/countries${qs.toString() ? `?${qs}` : ""}`, true, { "x-custom-lang": "en" });
  return response;
};

export const deleteCountry = async (id: string): Promise<void> => {
  await HttpService.deleteRecord(`api/v1/countries/${id}`, { "x-custom-lang": "en" }, true);
};

export const bulkCreateCountries = async (data: CountryBulkCreateDto): Promise<LocationCountry[]> => {
  const response = await HttpService.post("api/v1/countries/bulk", data, headers, true);
  return response;
};

export interface BulkImportCsvResult {
  success: boolean;
  message: string;
  stats: {
    countriesCreated: number;
    countriesFound: number;
    regionsCreated: number;
    regionsFound: number;
    citiesCreated: number;
    citiesFound: number;
    totalRows: number;
    errors: string[];
  };
}

export const bulkImportCsv = async (file: File): Promise<BulkImportCsvResult> => {
  const formData = new FormData();
  formData.append("file", file);
  
  // Don't set Content-Type header for FormData - browser will set it automatically with boundary
  const response = await HttpService.post("api/v1/countries/bulk-import-csv", formData, {}, true);
  return response;
};

// Regions CRUD Functions
export const createRegion = async (data: RegionCreateDto): Promise<LocationRegion> => {
  const response = await HttpService.post("api/v1/regions", data, headers, true);
  return response;
};

export const updateRegion = async (id: string, data: Partial<RegionCreateDto>): Promise<LocationRegion> => {
  const response = await HttpService.patch(`api/v1/regions/${id}`, data, headers, true);
  return response;
};

export const getRegionById = async (id: string): Promise<LocationRegion> => {
  const response = await HttpService.get(`api/v1/regions/${id}`, true, { "x-custom-lang": "en" });
  return response;
};

export const getRegions = async (query?: Record<string, string | number | boolean>): Promise<RegionResponse> => {
  const qs = new URLSearchParams();
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.append(k, String(v));
  });
  const response = await HttpService.get(`api/v1/regions${qs.toString() ? `?${qs}` : ""}`, true, { "x-custom-lang": "en" });
  return response;
};

export const deleteRegion = async (id: string): Promise<void> => {
  await HttpService.deleteRecord(`api/v1/regions/${id}`, { "x-custom-lang": "en" }, true);
};

// getRegionsByCountry - now handled directly in Redux slice from country object

// Cities CRUD Functions
export const createCity = async (data: CityCreateDto): Promise<LocationCity> => {
  const response = await HttpService.post("api/v1/cities", data, headers, true);
  return response;
};

export const updateCity = async (id: string, data: Partial<CityCreateDto>): Promise<LocationCity> => {
  const response = await HttpService.patch(`api/v1/cities/${id}`, data, headers, true);
  return response;
};

export const getCityById = async (id: string): Promise<LocationCity> => {
  const response = await HttpService.get(`api/v1/cities/${id}`, true, { "x-custom-lang": "en" });
  return response;
};

// getCities - now handled directly in Redux slice from countries/regions objects

export const deleteCity = async (id: string): Promise<void> => {
  await HttpService.deleteRecord(`api/v1/cities/${id}`, { "x-custom-lang": "en" }, true);
};

// getCitiesByRegion and getCitiesByCountry - now handled directly in Redux slice from region/country objects
